import { Box } from '@mui/system';
import { TableBody, TableContainer, useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import ResponsiveTableHead, { ResponsiveTableHeadColumn } from './ResponsiveTableHead';
import { NAVY_TABLE_SX, StyledTable } from './styles';

interface ResponsiveTableProps {
  tableHeadColumns?: Array<ResponsiveTableHeadColumn>;
  TableHead?: React.ReactNode;
  loadingSpinner?: boolean;
  isLoading?: boolean;
  orderBy?: string;
  sortOrder?: 'asc' | 'desc';
  onRequestSort?: (event: React.MouseEvent<unknown>, property: string) => void;
  children?: React.ReactNode;
  maxHeight?: string | number;
  stickyHeader?: boolean;
  enableStickyHorizontalScrollbar?: boolean;
  /**
   * 'navy': the newer designs' table (Figma 1695:3690): navy header, bordered and rounded,
   * 48px rows. Opt-in per table; every other table keeps the default look.
   */
  variant?: 'default' | 'navy';
}

function ResponsiveTable({
  tableHeadColumns,
  TableHead,
  loadingSpinner = false,
  isLoading = false,
  orderBy,
  sortOrder,
  onRequestSort,
  children,
  maxHeight = '80vh',
  stickyHeader = false,
  enableStickyHorizontalScrollbar = true,
  variant = 'default',
}: ResponsiveTableProps): JSX.Element {
  const theme = useTheme();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const stickyScrollbarRef = useRef<HTMLDivElement>(null);
  // Prevent bidirectional scroll sync loops
  const isSyncingScrollRef = useRef(false);
  const [showStickyScrollbar, setShowStickyScrollbar] = useState(false);
  const [stickyScrollbarWidth, setStickyScrollbarWidth] = useState(0);
  const [stickyScrollbarLeft, setStickyScrollbarLeft] = useState(0);
  const [stickyContentWidth, setStickyContentWidth] = useState(0);

  // Check if horizontal scroll is needed and if original scrollbar is visible
  useEffect(() => {
    if (!enableStickyHorizontalScrollbar || !tableContainerRef.current) return;

    const container = tableContainerRef.current;

    const checkScrollNeeded = () => {
      const hasHorizontalScroll = container.scrollWidth > container.clientWidth;

      if (hasHorizontalScroll) {
        // Check if the original scrollbar is visible in the viewport
        const containerRect = container.getBoundingClientRect();
        const originalScrollbarVisible = containerRect.bottom <= window.innerHeight;

        setShowStickyScrollbar(!originalScrollbarVisible);

        // Clamp the sticky scrollbar to the visible portion of the container in the viewport
        const visibleLeft = Math.max(0, containerRect.left);
        const visibleRight = Math.min(window.innerWidth, containerRect.right);
        const visibleWidth = Math.max(0, visibleRight - visibleLeft);
        setStickyScrollbarLeft(visibleLeft);
        setStickyScrollbarWidth(visibleWidth);

        // Ensure inner content matches the table's full scrollable width
        setStickyContentWidth(container.scrollWidth);
      } else {
        setShowStickyScrollbar(false);
        setStickyContentWidth(0);
      }
    };

    // Initial check
    checkScrollNeeded();

    // Create ResizeObserver to watch for size changes (if available)
    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(checkScrollNeeded);
      resizeObserver.observe(container);

      // Also observe the table itself for content changes
      const table = container.querySelector('table');
      if (table) {
        resizeObserver.observe(table);
      }
    }

    // Listen to scroll events on window to check visibility
    window.addEventListener('scroll', checkScrollNeeded);
    window.addEventListener('resize', checkScrollNeeded);

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('scroll', checkScrollNeeded);
      window.removeEventListener('resize', checkScrollNeeded);
    };
  }, [enableStickyHorizontalScrollbar, children]);

  // Sync scroll positions between table container and sticky scrollbar
  const handleTableScroll = () => {
    if (!stickyScrollbarRef.current || !tableContainerRef.current) return;
    if (isSyncingScrollRef.current) return;
    const next = tableContainerRef.current.scrollLeft;
    if (stickyScrollbarRef.current.scrollLeft !== next) {
      isSyncingScrollRef.current = true;
      stickyScrollbarRef.current.scrollLeft = next;
      // Release on next frame to cover event firing order differences
      requestAnimationFrame(() => (isSyncingScrollRef.current = false));
    }
  };

  const handleStickyScroll = () => {
    if (!tableContainerRef.current || !stickyScrollbarRef.current) return;
    if (isSyncingScrollRef.current) return;
    const next = stickyScrollbarRef.current.scrollLeft;
    if (tableContainerRef.current.scrollLeft !== next) {
      isSyncingScrollRef.current = true;
      tableContainerRef.current.scrollLeft = next;
      requestAnimationFrame(() => (isSyncingScrollRef.current = false));
    }
  };

  // Sync scroll position when sticky scrollbar becomes visible
  useEffect(() => {
    if (showStickyScrollbar && stickyScrollbarRef.current && tableContainerRef.current) {
      // Preserve the current scroll position when sticky scrollbar appears
      stickyScrollbarRef.current.scrollLeft = tableContainerRef.current.scrollLeft;
    }
  }, [showStickyScrollbar]);

  return (
    <>
      <TableContainer
        ref={tableContainerRef}
        component={Box}
        onScroll={handleTableScroll}
        sx={{
          overflowX: 'auto',
          overflowY: 'auto',
          maxHeight,
          position: 'relative',
          ...(stickyHeader && {
            '& .MuiTableHead-root': {
              position: 'sticky',
              top: 0,
              backgroundColor: theme.palette.background.paper,
              zIndex: 1,
              boxShadow: `0 2px 4px -1px ${theme.palette.divider}`,
            },
          }),
          ...(variant === 'navy' && NAVY_TABLE_SX(theme)),
        }}
      >
        <StyledTable stickyHeader={stickyHeader}>
          {tableHeadColumns && (
            <ResponsiveTableHead
              columns={tableHeadColumns}
              orderBy={orderBy}
              sortOrder={sortOrder}
              onRequestSort={onRequestSort}
            />
          )}
          {!tableHeadColumns && TableHead}
          <TableBody>{children}</TableBody>
        </StyledTable>
      </TableContainer>

      {/* Sticky horizontal scrollbar */}
      {enableStickyHorizontalScrollbar && showStickyScrollbar && (
        <Box
          ref={stickyScrollbarRef}
          onScroll={handleStickyScroll}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: `${stickyScrollbarLeft}px`,
            width: `${stickyScrollbarWidth}px`,
            height: '17px', // Standard scrollbar height
            backgroundColor: theme.palette.background.paper,
            borderTop: `1px solid ${theme.palette.divider}`,
            zIndex: 1000,
            overflowX: 'auto',
            overflowY: 'hidden',
          }}
        >
          {/* Invisible content with same width as table to create scrollbar */}
          <Box
            sx={{
              width: stickyContentWidth,
              height: '1px',
              backgroundColor: 'transparent',
            }}
          />
        </Box>
      )}
    </>
  );
}

export default ResponsiveTable;
